/*
 * FOUNDER CONSOLE V1 — the surface.
 *
 * Authorization is NOT decided here. This page asks the server, and the server reads the role
 * from the member's own record. A member who reaches this route without that role is shown
 * nothing, because there is nothing to show them: every figure arrives from an endpoint that
 * refuses them.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Home, Users, Heart, BarChart3, Target, MessageSquare, Activity, FileText, Lock,
  ShieldCheck, ChevronDown, ChevronLeft, ChevronRight, ArrowLeft, Mail, Inbox, AlertTriangle,
  ScrollText, Network, Globe2,
} from "lucide-react";
import "../../styles/founder-console.css";
import {
  HeadlineCards, GrowthPanel, DistributionPanel, QuickSignals, EngagementPanel, UsagePanel,
  SystemPanel, RadarPanel, FeedbackPanel, BriefPanel, PrivacyPanel,
} from "./OverviewPanels";
import {
  MembersView, RelationshipView, UsageView, SystemView, RadarView, FeedbackView, BriefView,
} from "./SectionViews";
import { AccessView } from "./AccessView";
import { IntelligenceView } from "./IntelligenceView";
import { MarketingConsentView } from "./MarketingConsentView";
import { ProductFeedbackView } from "./ProductFeedbackView";
import { IncidentsView } from "./IncidentsView";
import { AuditHistoryView } from "./AuditHistoryView";
import { CIGovernanceView } from "./CIGovernanceView";
import { CIGate3View } from "./CIGate3View";
import { RegionalView } from "./RegionalView";
import { FreshnessNote } from "./ConsolePrimitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/founder`;

/* The nav mirrors the server's permissions. It is a convenience, never the boundary: an
   unauthorized section is refused by the endpoint and is never sent to this browser. */
const NAV = [
  { key: "overview", label: "Overview", icon: Home, permission: "console.overview" },
  { key: "members", label: "Members", icon: Users, permission: "console.members" },
  { key: "relationship", label: "Relationship Health", icon: Heart, permission: "console.relationship" },
  { key: "usage", label: "Usage & Capacity", icon: BarChart3, permission: "console.usage" },
  { key: "radar", label: "Capability Radar", icon: Target, permission: "console.radar" },
  { key: "feedback", label: "Explicit Feedback", icon: MessageSquare, permission: "console.feedback" },
  { key: "product-feedback", label: "Product Feedback", icon: Inbox, permission: "console.feedback" },
  { key: "system", label: "System Health", icon: Activity, permission: "console.system" },
  { key: "incidents", label: "Incidents", icon: AlertTriangle, permission: "console.incidents" },
  { key: "regional", label: "Regional", icon: Globe2, permission: "console.regional" },
  { key: "reporting-audit", label: "Reporting Audit", icon: ScrollText, permission: "console.audit" },
  { key: "ci-governance", label: "Collective Intelligence", icon: Network, permission: "console.ci_governance" },
  { key: "ci-gate3", label: "Gate 3 governance", icon: Network, permission: "console.ci_governance" },
  { key: "intelligence", label: "Intelligence (clone)", icon: Network, permission: "console.ci_governance" },
  { key: "brief", label: "Founder Brief", icon: FileText, permission: "console.brief" },
  { key: "marketing", label: "Marketing Consent", icon: Mail, permission: "console.marketing_export" },
  { key: "access", label: "Access Management", icon: ShieldCheck, permission: "console.access" },
];

const RANGES = [
  { days: 7, label: "Last 7 days" },
  { days: 14, label: "Last 14 days" },
  { days: 28, label: "Last 28 days" },
  { days: 90, label: "Last 90 days" },
];

/* Calendar helpers. Dates are handled as plain YYYY-MM-DD, which is exactly what the server
   accepts, so no timezone is ever guessed at on the way there. */
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const TODAY = iso(new Date());
const shiftMonth = (m, by) => new Date(m.getFullYear(), m.getMonth() + by, 1);
const isCurrentMonth = (m) => {
  const now = new Date();
  return m.getFullYear() === now.getFullYear() && m.getMonth() === now.getMonth();
};
const monthLabel = (m) => m.toLocaleDateString("en-US", { month: "long", year: "numeric" });
const monthCells = (m) => {
  const first = new Date(m.getFullYear(), m.getMonth(), 1);
  const days = new Date(m.getFullYear(), m.getMonth() + 1, 0).getDate();
  const cells = Array(first.getDay()).fill(null);
  for (let d = 1; d <= days; d += 1) cells.push(iso(new Date(m.getFullYear(), m.getMonth(), d)));
  return cells;
};

export default function FounderConsole() {
  const navigate = useNavigate();
  const [state, setState] = useState("checking");   // checking | denied | ready | error
  const [identity, setIdentity] = useState(null);
  const [range, setRange] = useState({ days: 7, start: null, end: null });
  const [rangeOpen, setRangeOpen] = useState(false);
  const [rangeTab, setRangeTab] = useState("presets");
  const [pending, setPending] = useState({ from: null, to: null });
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [rangeError, setRangeError] = useState(null);
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState({});
  const [section, setSection] = useState("overview");

  const params = useMemo(() => (range.start && range.end
    ? { start: range.start, end: range.end }
    : { days: range.days }), [range]);

  const load = useCallback(async () => {
    try {
      const [who, overview, metrics] = await Promise.all([
        axios.get(`${API}/authorization`),
        axios.get(`${API}/overview`, { params }),
        axios.get(`${API}/metrics`),
      ]);
      setIdentity(who.data);
      setData(overview.data);
      setCatalog(metrics.data.metrics || {});
      setState("ready");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 422) { setRangeError(e.response.data?.detail || "That range cannot be used."); return; }
      setState(code === 401 || code === 403 ? "denied" : "error");
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const reloadRadar = useCallback(async () => {
    const radar = await axios.get(`${API}/capability-radar`, { params });
    setData((prev) => (prev ? { ...prev, capability_radar: radar.data } : prev));
  }, [params]);

  const comparison = useMemo(() => `${data?.window?.days || range.days} days`,
    [data, range.days]);

  if (state === "checking") {
    return <div className="fc__loading" data-testid="fc-loading">Opening the Console…</div>;
  }
  if (state === "denied") {
    return (
      <div className="fc__denied" data-testid="fc-denied">
        <div>
          <Lock style={{ width: 26, height: 26, opacity: 0.6, marginBottom: 14 }} />
          <div>This surface is private.</div>
          <button className="fc-btn fc-btn--ghost" style={{ marginTop: 22 }}
            onClick={() => navigate("/")} data-testid="fc-denied-home">Return to Arvexis</button>
        </div>
      </div>
    );
  }
  if (state === "error" || !data) {
    return (
      <div className="fc__denied" data-testid="fc-error">
        <div>
          <div>The Console could not read its evidence just now.</div>
          <button className="fc-btn fc-btn--ghost" style={{ marginTop: 22 }} onClick={load}
            data-testid="fc-error-retry">Try again</button>
        </div>
      </div>
    );
  }

  const { members, relationship_health: health, usage_capacity: usage, system_health: system,
          capability_radar: radar, explicit_feedback: feedback, brief, quick_signals: signals,
          privacy, window: win, environment } = data;

  const can = (permission) => (identity?.permissions || []).includes(permission);
  const nav = NAV.filter((item) => can(item.permission)
                                   && (item.key !== "access" || identity?.is_owner));

  const body = () => {
    switch (section) {
      case "members":
        return members ? <MembersView members={members} catalog={catalog} comparison={comparison} /> : null;
      case "relationship":
        return health ? <RelationshipView health={health} members={members || {}} catalog={catalog} comparison={comparison} /> : null;
      case "usage":
        return usage ? <UsageView usage={usage} catalog={catalog} comparison={comparison} /> : null;
      case "radar":
        return radar ? <RadarView radar={radar} catalog={catalog} comparison={comparison} onReload={reloadRadar} canDispose={can("console.radar.dispose")} /> : null;
      case "feedback":
        return feedback ? <FeedbackView feedback={feedback} catalog={catalog} comparison={comparison} /> : null;
      case "system":
        return system ? <SystemView system={system} catalog={catalog} comparison={comparison} /> : null;
      case "brief":
        return brief ? <BriefView brief={brief} /> : null;
      case "product-feedback":
        return <ProductFeedbackView params={params} />;
      case "incidents":
        return <IncidentsView canManage={can("console.incidents.manage")} />;
      case "regional":
        return <RegionalView />;
      case "reporting-audit":
        return <AuditHistoryView params={params} />;
      case "ci-governance":
        return <CIGovernanceView />;
      case "ci-gate3":
        return <CIGate3View />;
      case "intelligence":
        return <IntelligenceView />;
      case "marketing":
        return <MarketingConsentView />;
      case "access":
        return (
          <>
            <header className="fc__section-head">
              <h2 data-testid="fc-access-title">Access Management</h2>
              <p>Manage who can access the Founder Console and what they can see.</p>
            </header>
            <AccessView />
          </>
        );
      default:
        return (
          <>
            {members && <HeadlineCards members={members} catalog={catalog} comparison={comparison} />}
            <div className="fc__row fc__row--growth">
              {members && <GrowthPanel growth={members.growth} window={win} catalog={catalog} />}
              {members && <DistributionPanel members={members} catalog={catalog} />}
              <QuickSignals signals={signals} catalog={catalog} onViewAll={() => setSection(can("console.brief") ? "brief" : "overview")} />
            </div>
            <div className="fc__row fc__row--mid">
              {health && <EngagementPanel health={health} members={members || {}} catalog={catalog} comparison={comparison} />}
              {usage && <UsagePanel usage={usage} catalog={catalog} comparison={comparison} />}
            </div>
            <div className="fc__row fc__row--health">
              {system && <SystemPanel system={system} catalog={catalog} />}
              {radar && <RadarPanel radar={radar} catalog={catalog} onViewAll={() => setSection("radar")} />}
              {feedback && <FeedbackPanel feedback={feedback} catalog={catalog} comparison={comparison} />}
            </div>
            <div className="fc__row fc__row--brief">
              {brief && <BriefPanel brief={brief} onReadFull={() => setSection("brief")} />}
              <PrivacyPanel privacy={privacy} />
            </div>
          </>
        );
    }
  };

  const first = (identity?.name || "Founder");

  return (
    <div className="fc" data-testid="founder-console">
      <nav className="fc__nav" data-testid="fc-nav">
        <div className="fc__brand">
          <div className="fc__brand-name">ARVEXIS</div>
          <div className="fc__brand-sub">FOUNDER CONSOLE<br />V1</div>
        </div>
        <div className="fc__navlist">
          {nav.map((item) => (
            <button
              key={item.key}
              className="fc__navitem"
              data-active={section === item.key}
              data-testid={`fc-nav-${item.key}`}
              onClick={() => setSection(item.key)}
            >
              <item.icon />{item.label}
            </button>
          ))}
        </div>
        <div className="fc__navfoot">
          <svg className="fc__orb" viewBox="0 0 150 150" aria-hidden="true">
            <defs>
              <radialGradient id="fcOrbGlow" cx="34%" cy="66%" r="62%">
                <stop offset="0%" stopColor="rgba(216,184,118,0.85)" />
                <stop offset="55%" stopColor="rgba(216,184,118,0.14)" />
                <stop offset="100%" stopColor="rgba(216,184,118,0)" />
              </radialGradient>
            </defs>
            <circle cx="50" cy="98" r="44" fill="url(#fcOrbGlow)" />
            <ellipse cx="75" cy="75" rx="58" ry="58" fill="none" stroke="rgba(214,223,240,0.22)" strokeWidth="0.8" />
            <ellipse cx="75" cy="75" rx="58" ry="22" fill="none" stroke="rgba(214,223,240,0.16)" strokeWidth="0.8" transform="rotate(-28 75 75)" />
            <circle cx="96" cy="52" r="1.8" fill="#f4efe7" />
          </svg>
          <div className="fc__creed">
            A MORE<br />HUMAN TOMORROW<br />IS A CHOICE WE<br />BUILD TODAY.<br />— ARVEXIS
          </div>
        </div>
      </nav>

      <main className="fc__main" data-testid="fc-main">
        <div className="fc__topbar">
          {/* The way back out. An ordinary in-app route: the same session, the same member
              experience, no second sign-in, and browser Back still works as it always did. */}
          <button className="fc__back" data-testid="fc-back-to-arvexis"
            onClick={() => navigate("/home")}>
            <ArrowLeft style={{ width: 14, height: 14 }} /> Back to Arvexis
          </button>
          <div className="fc__assurance" data-testid="fc-assurance">
            <Lock /> Private. Authorized. Product intelligence, not member surveillance.
          </div>
          <div className="fc__spacer" />
          <span className="fc__pill" data-testid="fc-environment">
            <i />{environment?.stripe_mode === "test" ? "Test environment" : `Stripe ${environment?.stripe_mode}`}
          </span>
          <span className="fc-chip" data-tone="quiet" data-testid="fc-role">
            {identity?.role_label || identity?.role}
          </span>
          <div className="fc__range-wrap" data-testid="fc-range">
            <button
              className="fc__range" data-testid="fc-range-toggle"
              aria-expanded={rangeOpen} onClick={() => setRangeOpen((o) => !o)}
            >
              <span className="fc__range-kicker">{win?.custom ? "Custom" : "Period"}</span>
              <span className="fc__range-label" data-testid="fc-range-current">
                {win?.custom
                  ? `${win.start_date} → ${win.end_date}`
                  : RANGES.find((r) => r.days === range.days)?.label}
              </span>
              <em className="fc__range-vs">vs previous {win?.days} days</em>
              <ChevronDown style={{ width: 13, height: 13, opacity: 0.6 }} />
            </button>
            {rangeOpen && (
              <div className="fc__range-pop" data-testid="fc-range-panel">
                <div className="fc__range-tabs">
                  <button data-active={rangeTab === "presets"} data-testid="fc-range-tab-presets"
                    onClick={() => setRangeTab("presets")}>Presets</button>
                  <button data-active={rangeTab === "custom"} data-testid="fc-range-tab-custom"
                    onClick={() => setRangeTab("custom")}>Custom range</button>
                </div>
                <div className="fc__range-head">
                  Reporting period
                  <i>{win?.label} · compared with {win?.comparison}</i>
                </div>
                <div className="fc__range-limit" data-testid="fc-range-note">
                  Not every figure can answer a past range. Dated activity can — new members,
                  adoption, dreams, Talk connections, capability signals, billing. Figures that
                  describe only the present cannot: member totals, plan distribution, allowance
                  pressure, lifetime conversion. In a range that ended in the past those are shown
                  as <b>Current only</b>, never back-filled. Every privacy, small-N and evidence
                  rule applies identically at every period.
                </div>

                {rangeTab === "presets" ? (
                  RANGES.map((r) => (
                    <button
                      key={r.days} className="fc__range-opt"
                      data-active={!win?.custom && r.days === range.days}
                      data-testid={`fc-range-option-${r.days}`}
                      onClick={() => {
                        setRangeError(null);
                        setRange({ days: r.days, start: null, end: null });
                        setRangeOpen(false);
                      }}
                    >
                      <span>{r.label}</span>
                      <i>compared with the previous {r.days} days</i>
                    </button>
                  ))
                ) : (
                  <div data-testid="fc-range-custom">
                    <div className="fc__range-dates">
                      <label>
                        From
                        <input type="date" value={pending.from || ""} max={TODAY}
                          data-testid="fc-range-from"
                          onChange={(e) => setPending((p) => ({ ...p, from: e.target.value }))} />
                      </label>
                      <label>
                        To
                        <input type="date" value={pending.to || ""} max={TODAY}
                          data-testid="fc-range-to"
                          onChange={(e) => setPending((p) => ({ ...p, to: e.target.value }))} />
                      </label>
                    </div>

                    <div className="fc__cal">
                      <div className="fc__cal-head">
                        <button data-testid="fc-cal-prev"
                          onClick={() => setMonth(shiftMonth(month, -1))}>
                          <ChevronLeft style={{ width: 14, height: 14 }} />
                        </button>
                        <b data-testid="fc-cal-month">{monthLabel(month)}</b>
                        <button data-testid="fc-cal-next" disabled={isCurrentMonth(month)}
                          onClick={() => setMonth(shiftMonth(month, 1))}>
                          <ChevronRight style={{ width: 14, height: 14 }} />
                        </button>
                      </div>
                      <div className="fc__cal-grid">
                        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                          <span key={d} className="fc__cal-dow">{d}</span>
                        ))}
                        {monthCells(month).map((cell, i) => cell ? (
                          <button
                            key={cell} className="fc__cal-day"
                            disabled={cell > TODAY}
                            data-active={cell === pending.from || cell === pending.to}
                            data-inside={!!(pending.from && pending.to
                              && cell > pending.from && cell < pending.to)}
                            data-testid={`fc-cal-day-${cell}`}
                            onClick={() => setPending((p) => (
                              !p.from || p.to || cell < p.from
                                ? { from: cell, to: null }
                                : { from: p.from, to: cell }))}
                          >
                            {Number(cell.slice(8, 10))}
                          </button>
                        ) : <span key={`pad-${i}`} />)}
                      </div>
                    </div>

                    {rangeError && (
                      <div className="fc__range-error" data-testid="fc-range-error">{rangeError}</div>
                    )}
                    <div className="fc__range-actions">
                      <button className="fc-btn fc-btn--ghost" data-testid="fc-range-cancel"
                        onClick={() => { setPending({ from: null, to: null }); setRangeError(null); setRangeOpen(false); }}>
                        Cancel
                      </button>
                      <button className="fc-btn" disabled={!pending.from || !pending.to}
                        data-testid="fc-range-apply"
                        onClick={() => {
                          setRangeError(null);
                          setRange({ days: null, start: pending.from, end: pending.to });
                          setRangeOpen(false);
                        }}>
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="fc__avatar" data-testid="fc-avatar">{first.slice(0, 1).toUpperCase()}</div>
        </div>

        <header className="fc__hero">
          <div className="fc__hero-sky" aria-hidden="true" />
          <div className="fc__creed-right">PEOPLE<br />PERSPECTIVE<br />POSSIBILITY</div>
          <h1 className="fc__hello" data-testid="fc-hello">
            {first}, <em>here’s what happened to me.</em>
          </h1>
          <p className="fc__hello-sub" data-testid="fc-hello-sub">
            A product-intelligence view of Arvexis. Real members. Real signals. Your judgment.
            {" "}<span style={{ color: "var(--fc-mute)" }}>{win?.label} · compared with {win?.comparison}</span>
          </p>
        </header>

        <div className="fc__body" data-testid={`fc-section-${section}`}>
          {win?.historical && (
            <div className="fc__histbar" data-testid="fc-historical-banner">
              <b>Custom range {win.start_date} to {win.end_date}.</b> Dated activity answers this
              range. Figures that describe only the present — member totals, plan distribution,
              allowance pressure, lifetime conversion — are shown as <b>Current only</b> for it.
              Nothing is back-filled, inferred or approximated.
            </div>
          )}
          {body()}
          {!["marketing", "access", "product-feedback", "incidents", "reporting-audit", "ci-governance", "ci-gate3", "intelligence", "regional"].includes(section) && <FreshnessNote freshness={data.freshness} />}
        </div>

        <footer className="fc__footer">
          <b>ARVEXIS</b>
          <span>Founder Console V1</span>
          <div className="fc__spacer" />
          <span>PEOPLE · PERSPECTIVE · POSSIBILITY</span>
        </footer>
      </main>
    </div>
  );
}
